import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { EmptyState } from "../components/EmptyState";
import { PosterImage } from "../components/PosterImage";
import { Playlists } from "../lib/api";
import { decodeMaybeUtf8 as decodeMaybe } from "../lib/decode";
import { useCatalog } from "../contexts/CatalogContext";
import { fmtDayIn, fmtTimeIn, getTimezone, sameDayIn } from "../lib/prefs";
import type { ShortEpgEntry } from "../lib/types";

interface Program {
  title: string;
  description?: string;
  startMs: number;
  endMs: number;
  past: boolean;
}

function parseStart(s: string | number | undefined): number {
  if (s === undefined || s === null) return NaN;
  // Numeric forms come back as either a unix-timestamp string ("1715180400")
  // or as an ISO-ish "YYYY-MM-DD HH:MM:SS" assumed UTC by Xtream.
  if (typeof s === "number") return s * 1000;
  if (/^\d{10}$/.test(s)) return Number(s) * 1000;
  if (/^\d{13}$/.test(s)) return Number(s);
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(s);
  if (!m) return NaN;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
}

function fmtDayLabel(ms: number, tz: string): string {
  const now = Date.now();
  if (sameDayIn(ms, now, tz)) return "Today";
  if (sameDayIn(ms, now - 86400 * 1000, tz)) return "Yesterday";
  return fmtDayIn(ms, tz);
}

export default function CatchupChannelPage() {
  const { streamId = "" } = useParams<{ streamId: string }>();
  const { catalog, activePlaylist } = useCatalog();
  const navigate = useNavigate();
  const [epg, setEpg] = useState<ShortEpgEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const channel = useMemo(
    () => catalog?.live.streams.find((s) => s.id === streamId) || null,
    [catalog, streamId]
  );

  useEffect(() => {
    if (!activePlaylist || activePlaylist.kind !== "xtream") {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // simple_data_table returns past + future programs (whatever upstream stores).
    // If it fails (e.g. some panels return 513), fall back to short EPG so we still
    // show *something* rather than blocking the catchup page entirely.
    Playlists.fullEpg(activePlaylist.id, Number(streamId))
      .then((d) => setEpg(d?.epg_listings || []))
      .catch(async (e) => {
        const detail = e?.response?.data?.detail || e?.message || "Failed to load EPG";
        try {
          const fallback = await Playlists.shortEpg(activePlaylist.id, Number(streamId), 100);
          if (fallback?.epg_listings?.length) {
            setEpg(fallback.epg_listings);
            return;
          }
        } catch {
          /* swallow */
        }
        setError(detail);
      })
      .finally(() => setLoading(false));
  }, [activePlaylist, streamId]);

  if (!activePlaylist) {
    return (
      <PageLayout>
        <EmptyState title="Pick a playlist" cta={{ to: "/settings", label: "Open settings" }} />
      </PageLayout>
    );
  }
  if (activePlaylist.kind !== "xtream") {
    return (
      <PageLayout>
        <EmptyState
          title="Catchup not available"
          hint="Catchup needs an Xtream playlist with EPG; M3U playlists don't expose timeshift."
        />
      </PageLayout>
    );
  }

  const now = Date.now();
  const tz = getTimezone();
  const programs: Program[] = epg
    .map((p) => {
      // simple_data_table uses *_timestamp (unix seconds); short EPG uses start/end strings
      const startMs = parseStart(
        (p.start_timestamp as string | number | undefined) ?? p.start
      );
      const endMs = parseStart(
        (p.stop_timestamp as string | number | undefined) ?? p.end
      );
      return {
        title: decodeMaybe(p.title),
        description: decodeMaybe(p.description),
        startMs,
        endMs,
        past: !Number.isNaN(startMs) && startMs < now,
      };
    })
    .filter((p) => !Number.isNaN(p.startMs) && !Number.isNaN(p.endMs))
    .sort((a, b) => a.startMs - b.startMs);

  const archiveDays = channel?.tv_archive_duration || 7;
  const earliestAllowed = now - archiveDays * 86400 * 1000;
  const playable = programs.filter((p) => p.startMs >= earliestAllowed && p.startMs < now);

  const grouped: { day: string; items: Program[] }[] = [];
  for (const p of playable) {
    const day = fmtDayLabel(p.startMs, tz);
    let g = grouped.find((x) => x.day === day);
    if (!g) {
      g = { day, items: [] };
      grouped.push(g);
    }
    g.items.push(p);
  }
  // Most recent day first (Today → Yesterday → older), and within each day
  // the most recently-aired program first (latest → earliest). User asked for
  // this ordering specifically — different from a normal forward EPG.
  for (const g of grouped) g.items.reverse();
  grouped.reverse();
  const defaultDay = (grouped.find((g) => g.day === "Today") || grouped[0])?.day ?? null;
  const activeDay = selectedDay ?? defaultDay;
  const visible = grouped.find((g) => g.day === activeDay);

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-3 text-[13px] font-ui transition-fast"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <PosterImage
              src={channel?.logo}
              alt={channel?.name || "Channel"}
              className="w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 rounded-ctrl bg-canvas border border-line overflow-hidden"
              imgClass="object-contain p-2"
              orientation="stack"
            />
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-[22px] sm:text-[36px] tracking-[-0.02em] leading-tight text-text-primary line-clamp-2">
                {channel?.name || `Channel ${streamId}`}
              </h1>
              <p className="font-data text-[11px] uppercase tracking-[0.13em] text-text-muted mt-0.5">
                {archiveDays} day archive
              </p>
            </div>
            <Link
              to={`/watch/live/${encodeURIComponent(streamId)}`}
              className="hidden sm:inline-flex px-4 py-2 rounded-pill bg-accent-gold text-canvas text-[12px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast items-center gap-1.5 flex-shrink-0"
            >
              <Play size={14} fill="currentColor" /> Watch live
            </Link>
          </div>
          <Link
            to={`/watch/live/${encodeURIComponent(streamId)}`}
            className="sm:hidden mt-3 h-11 w-full rounded-ctrl bg-accent-gold text-canvas text-[13px] font-ui font-semibold uppercase tracking-[0.04em] flex items-center justify-center gap-2"
          >
            <Play size={15} fill="currentColor" /> Watch live
          </Link>
        </div>

        {loading ? (
          <p className="text-[14px] font-ui text-text-secondary">Loading EPG…</p>
        ) : error ? (
          <EmptyState title="Couldn't load EPG" hint={error} />
        ) : grouped.length === 0 ? (
          <EmptyState title="No archived programs" hint="EPG data is empty or out of range." />
        ) : (
          <div className="w-full">
            {/* Day chips — full width, scroll horizontally on overflow.
                No item counts: user requested a cleaner layout than the
                other apps' "X programs today" badge. */}
            <div className="overflow-x-auto no-scrollbar -mx-4 px-4 lg:mx-0 lg:px-0 mb-4 sticky top-0 lg:top-2 z-10 bg-canvas/85 backdrop-blur-md py-2">
              <div className="flex items-center gap-2">
                {grouped.map((g) => (
                  <button
                    key={g.day}
                    type="button"
                    onClick={() => setSelectedDay(g.day)}
                    className={`flex-shrink-0 px-3.5 py-1.5 rounded-pill text-[12px] font-ui font-medium uppercase tracking-[0.04em] transition-fast border ${
                      activeDay === g.day
                        ? "bg-accent-gold text-canvas border-accent-gold shadow-[0_0_8px_rgba(224,172,72,0.35)]"
                        : "border-muted/40 text-text-secondary hover:text-text-primary hover:border-text-secondary"
                    }`}
                  >
                    {g.day}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              {(visible?.items ?? []).map((p, idx) => {
                const duration = Math.max(1, Math.round((p.endMs - p.startMs) / 60000));
                return (
                  <Link
                    key={idx}
                    to={`/watch/catchup/${encodeURIComponent(streamId)}?start_ms=${p.startMs}&duration=${duration}`}
                    state={{
                      catchupProgram: {
                        title: p.title,
                        description: p.description,
                        startMs: p.startMs,
                        endMs: p.endMs,
                      },
                    }}
                    className="group block bg-surface hover:bg-card-hover transition-fast rounded-xl p-3 border border-muted/30"
                  >
                    <div className="flex items-start gap-3">
                      <div className="font-mono text-[12px] text-accent-gold w-28 flex-shrink-0">
                        {fmtTimeIn(p.startMs, tz)} → {fmtTimeIn(p.endMs, tz)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-ui font-medium text-text-primary truncate">
                          {p.title || "Untitled"}
                        </p>
                        {p.description && (
                          <p className="text-[12px] font-ui text-text-secondary line-clamp-2 mt-0.5">
                            {p.description}
                          </p>
                        )}
                      </div>
                      <Play
                        size={14}
                        className="text-text-secondary group-hover:text-accent-gold transition-fast flex-shrink-0 mt-1"
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
